import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, FileText, Star, MessageCircle, Coins, Users, BadgeDollarSign, BadgeEuro, BadgePoundSterling, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ServiceRequestService } from '@/services/serviceRequestService';
import { ServiceRequestWithProposals, JobProposalDatabaseStatus, Quote, QuoteItem, JobProposal } from '@/types/serviceRequest';
import { FadeIn } from '@/components/Animations';
import { BusinessBadges } from '@/components/BusinessBadges';
import { useCommunity } from '@/contexts/CommunityContext';
import Layout from '@/components/Layout';
import { PATH } from '@/routes/routeConfig';
import { ProposalFeedbackModal } from '@/components/business/ProposalFeedbackModal';

const ServiceRequestDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getParameter } = useCommunity();
  const currency = getParameter<string>('currency') || '$';
  
  // Get appropriate currency icon based on community currency
  const getCurrencyIcon = () => {
    switch (currency) {
      case '£':
        return BadgePoundSterling;
      case '€':
      case 'EUR':
        return BadgeEuro;
      case '$':
      case 'USD':
        return BadgeDollarSign;
      default:
        return Coins; // Generic fallback
    }
  };
  
  const CurrencyIcon = getCurrencyIcon();
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  
  const [serviceRequest, setServiceRequest] = useState<ServiceRequestWithProposals | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedQuoteId, setExpandedQuoteId] = useState<string | null>(null);
  const [selectedProposal, setSelectedProposal] = useState<JobProposal | null>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const loadServiceRequest = useCallback(async () => {
    if (!serviceRequestId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const request = await ServiceRequestService.getServiceRequestWithProposals(serviceRequestId);
      setServiceRequest(request);
    } catch (error) {
      console.error('Error loading service request:', error);
      setError('Failed to load service request');
      toast({
        title: "Error",
        description: "Failed to load service request details. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [serviceRequestId, toast]);

  useEffect(() => {
    if (serviceRequestId) {
      loadServiceRequest();
    }
  }, [serviceRequestId, loadServiceRequest]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  const handleProposalFeedback = (proposal: JobProposal) => {
    setSelectedProposal(proposal);
    setIsFeedbackModalOpen(true);
  };

  const handleFeedbackSuccess = (conversationId?: string) => {
    // Reload service request to get updated data
    loadServiceRequest();
  };

  const getBusinessStatusDisplay = (proposal: { status: string; quote?: number | null; detailed_quote?: Quote; response_message?: string | null; user_feedback?: string | null }) => {
    const status = proposal.status as JobProposalDatabaseStatus;
    const hasQuote = proposal.quote !== null && proposal.quote !== undefined;
    const hasDetailedQuote = proposal.detailed_quote !== null && proposal.detailed_quote !== undefined;
    const hasAnyQuote = hasQuote || hasDetailedQuote;
    const hasResponseMessage = proposal.response_message !== null && proposal.response_message !== undefined && proposal.response_message.trim() !== '';
    
    switch (status) {
      case 'AWAITING_RESPONSE':
        return {
          text: 'Waiting...',
          color: 'text-gray-500',
          bgColor: 'bg-gray-50',
          icons: [],
          showFeedbackButton: false
        };
      case 'PROPOSED':
        return {
          text: 'Proposed',
          color: 'text-green-700',
          bgColor: 'bg-green-50',
          icons: [
            ...(hasAnyQuote ? [{ icon: CurrencyIcon, title: 'Quote provided' }] : []),
            ...(hasResponseMessage ? [{ icon: MessageCircle, title: 'Response message included' }] : [])
          ],
          showFeedbackButton: true
        };
      case 'REQUESTED_INFO':
        return {
          text: 'Needs info',
          color: 'text-orange-700',
          bgColor: 'bg-orange-50',
          icons: [{ icon: MessageCircle, title: 'Response message included' }],
          showFeedbackButton: true
        };
      case 'USER_INTERESTED':
        return {
          text: 'You expressed interest',
          color: 'text-blue-700',
          bgColor: 'bg-blue-50',
          icons: [{ icon: ThumbsUp, title: 'You are interested' }],
          showFeedbackButton: false
        };
      case 'USER_REJECTED':
        return {
          text: 'You declined',
          color: 'text-gray-700',
          bgColor: 'bg-gray-50',
          icons: [{ icon: ThumbsDown, title: 'You declined this proposal' }],
          showFeedbackButton: false
        };
      case 'DECLINED':
        return {
          text: 'Declined',
          color: 'text-red-700',
          bgColor: 'bg-red-50',
          icons: hasResponseMessage ? [{ icon: MessageCircle, title: 'Response message included' }] : [],
          showFeedbackButton: false
        };
      default:
        return {
          text: 'Unknown',
          color: 'text-gray-500',
          bgColor: 'bg-gray-50',
          icons: [],
          showFeedbackButton: false
        };
    }
  };

  // Helper function to render quote display based on type
  const renderQuoteDisplay = (proposal: { quote?: number | null; detailed_quote?: Quote }) => {
    // Check for new detailed quote system first
    if (proposal.detailed_quote) {
      const quote = proposal.detailed_quote;
      return (
        <div className="mb-6 p-4 md:p-6 bg-white/80 rounded-lg border-2 border-primary/20">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CurrencyIcon className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {quote.price_type === 'fixed' && 'Fixed Quote'}
                {quote.price_type === 'estimate' && 'Estimated Quote'}
                {quote.price_type === 'range' && 'Price Range'}
              </span>
            </div>
            
            <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {quote.price_type === 'fixed' && `${currency}${quote.fixed_price}`}
              {quote.price_type === 'estimate' && `~${currency}${quote.estimated_price}`}
              {quote.price_type === 'range' && `${currency}${quote.min_price} - ${currency}${quote.max_price}`}
            </div>
            
            {quote.notes && (
              <p className="text-sm text-gray-600 mb-3">{quote.notes}</p>
            )}
            
            {quote.quote_items && quote.quote_items.length > 0 && (
              <Button
                variant="ghost" 
                size="sm"
                className="text-primary hover:text-primary/80"
                onClick={() => setExpandedQuoteId(expandedQuoteId === quote.id ? null : quote.id)}
              >
                <FileText className="w-4 h-4 mr-1" />
                {expandedQuoteId === quote.id ? 'Hide' : 'View'} breakdown ({quote.quote_items.length} items)
                {expandedQuoteId === quote.id ? (
                  <ChevronUp className="w-4 h-4 ml-1" />
                ) : (
                  <ChevronDown className="w-4 h-4 ml-1" />
                )}
              </Button>
            )}
          </div>
          
          {/* Expandable quote details */}
          {expandedQuoteId === quote.id && quote.quote_items && quote.quote_items.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-medium text-gray-800">Quote Breakdown</h5>
                {quote.status === 'partial' && (
                  <span className="text-xs px-2 py-1 bg-orange-100 text-orange-800 rounded-full font-medium">
                    Partial Quote
                  </span>
                )}
              </div>
              <div className="space-y-2">
                {quote.quote_items
                  .sort((a: QuoteItem, b: QuoteItem) => a.display_order - b.display_order)
                  .map((item: QuoteItem) => (
                  <div key={item.id} className="flex justify-between items-center py-1">
                    <div className="flex-1">
                      <span className="text-sm text-gray-700">{item.label}</span>
                      <div className="text-xs text-gray-500">
                        {item.quantity} {item.unit_label} × {currency}{item.unit_price}
                      </div>
                    </div>
                    <span className="font-medium text-gray-900">{currency}{item.line_total.toFixed(2)}</span>
                  </div>
                ))}
                
                {quote.price_type === 'fixed' && (
                  <div className="border-t pt-2 mt-3">
                    <div className="flex justify-between font-semibold text-gray-900">
                      <span>Total</span>
                      <span>{currency}{quote.fixed_price}</span>
                    </div>
                  </div>
                )}
                
                {quote.price_type === 'range' && quote.quote_items.length > 0 && (
                  <div className="border-t pt-2 mt-3 text-xs text-gray-600">
                    <div>Subtotal: {currency}{quote.quote_items.reduce((sum: number, item: QuoteItem) => sum + item.line_total, 0).toFixed(2)}</div>
                    <div className="font-medium">Total range: {currency}{quote.min_price} - {currency}{quote.max_price}</div>
                    {quote.status === 'partial' && (
                      <div className="text-orange-600 font-medium mt-1">
                        Additional costs may apply - quote is incomplete
                      </div>
                    )}
                  </div>
                )}
                
                {/* Add partial quote disclaimer for all types when breakdown exists */}
                {quote.status === 'partial' && quote.quote_items.length > 0 && quote.price_type !== 'range' && (
                  <div className="border-t pt-2 mt-3 text-xs text-orange-600 font-medium">
                    ⚠️ This breakdown is incomplete - additional costs may apply
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      );
    }
    
    // Fallback to old quote system for backward compatibility
    if (proposal.quote) {
      return (
        <div className="mb-6 p-4 md:p-6 bg-white/80 rounded-lg border-2 border-primary/20">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CurrencyIcon className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Quote Provided</span>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-gray-900">{currency}{proposal.quote}</div>
          </div>
        </div>
      );
    }
    
    return null;
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-6 px-4 bg-white">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading service request details...</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !serviceRequest) {
    return (
      <Layout>
        <div className="container mx-auto py-6 px-4 bg-white">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-xl md:text-2xl font-bold mb-4">Error loading service request</h1>
            <Button onClick={() => navigate(PATH.MY_SERVICE_REQUESTS)}>
              Back to My Service Requests
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="container mx-auto py-8 md:py-10 px-4 bg-white"
      >
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <FadeIn direction="up" delay={0.1}>
            <div className="mb-6 md:mb-8 lg:mb-10">
              <div className="flex items-center gap-4 mb-4">
                <Button
                  variant="outline"
                  onClick={() => navigate(PATH.MY_SERVICE_REQUESTS)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">
                {t('serviceRequest.pageTitle', 'Service Request Details')}
              </h1>
              <p className="text-gray-600 text-sm md:text-base">
                View your request details and business responses
              </p>
            </div>
          </FadeIn>

          {/* Service Request Information */}
          <FadeIn direction="up" delay={0.2}>
            <Card className="shadow-lg border border-gray-200 bg-white mb-3 md:mb-4">
              <CardContent className="p-3 md:p-4">
                {/* Quote bar container spanning full height */}
                <div className="border-l-2 border-gray-200 pl-3 h-full">
                  {/* Original Service Request */}
                  <div className="mb-3">
                    <div className="text-xs text-gray-500 mb-1">{t('serviceRequest.originalRequestLabel', 'Original request:')}</div>
                    <p className="text-sm md:text-base italic text-gray-600 leading-relaxed">"{serviceRequest.original_request}"</p>
                  </div>

                  {/* Attachments - Mobile-optimized grid */}
                  {serviceRequest.attachments && serviceRequest.attachments.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 md:w-4 md:h-4" />
{t('serviceRequest.attachments', 'Attachments')} ({serviceRequest.attachments.length})
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                        {serviceRequest.attachments.map((attachment) => (
                          <div key={attachment.id} className="relative group">
                            <img
                              src={attachment.image_url}
                              alt={attachment.original_filename}
                              className="w-full h-20 md:h-24 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => window.open(attachment.image_url, '_blank')}
                            />
                            <div className="absolute bottom-1 left-1 right-1 bg-black/70 text-white text-xs p-1 rounded truncate">
                              {attachment.original_filename}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Location at bottom right */}
                  {serviceRequest.town && (
                    <div className="flex justify-end mt-2">
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span>{serviceRequest.town}</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </FadeIn>

          {/* Job Proposals Section */}
          <FadeIn direction="up" delay={0.3}>
            <div className="mb-6 md:mb-8 lg:mb-10">
              <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">
{t('serviceRequest.businessProposals', 'Business Proposals')} ({serviceRequest.proposals?.length || 0})
              </h2>

              {serviceRequest.proposals && serviceRequest.proposals.length > 0 ? (
                <div className="space-y-4 md:space-y-6">
                  {serviceRequest.proposals.map((proposal, index) => {
                    const statusDisplay = getBusinessStatusDisplay(proposal);
                    
                    return (
                      <FadeIn key={proposal.id} direction="up" delay={0.4 + index * 0.1}>
                        <Card className={`shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden ${statusDisplay.bgColor}`}>
                          <CardContent className="p-4 md:p-6">
                            {/* Business Header with Logo and Status */}
                            <div className="flex items-center justify-between gap-3 mb-4">
                              {/* Left Side: Logo and Business Name */}
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                {/* Company Logo */}
                                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                                  {proposal.business?.company_logo ? (
                                    <img
                                      src={proposal.business.company_logo}
                                      alt={proposal.business?.name || 'Business'}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        target.nextElementSibling?.classList.remove('hidden');
                                      }}
                                    />
                                  ) : null}
                                  <div className={`w-full h-full bg-gray-300 rounded-full flex items-center justify-center ${proposal.business?.company_logo ? 'hidden' : ''}`}>
                                    <span className="text-sm font-medium text-gray-600">
                                      {proposal.business?.name?.charAt(0) || 'B'}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-base md:text-lg font-bold text-gray-900 truncate mb-1">
                                    {proposal.business?.name || 'Service Provider'}
                                  </h3>
                                  {/* Rating and Reviews */}
                                  {proposal.business?.reviews && proposal.business.reviews.length > 0 ? (
                                    <div className="flex items-center gap-1">
                                      <Star className="w-3.5 h-3.5 text-yellow-400 fill-current" />
                                      <span className="text-xs font-medium text-gray-700">
                                        {(proposal.business.reviews.reduce((sum: number, review: { rating: number }) => sum + review.rating, 0) / proposal.business.reviews.length).toFixed(1)}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        ({proposal.business.reviews.length} review{proposal.business.reviews.length !== 1 ? 's' : ''})
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <Star className="w-3.5 h-3.5 text-gray-300" />
                                      <span className="text-xs text-gray-500">No reviews yet</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Right Side: Business Location */}
                              <div className="flex items-start gap-1 flex-shrink-0">
                                {(proposal.business?.city || proposal.business?.postal_code) && (
                                  <>
                                    <MapPin className="w-3.5 h-3.5 text-gray-500 mt-0.5" />
                                    <div className="text-xs text-gray-600">
                                      {proposal.business.city && (
                                        <div className="leading-tight">{proposal.business.city}</div>
                                      )}
                                      {proposal.business.postal_code && (
                                        <div className="leading-tight">{proposal.business.postal_code}</div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Business Badges */}
                            {proposal.business?.id && (
                              <div className="mb-6">
                                <BusinessBadges 
                                  businessId={proposal.business.id} 
                                  mode="compact" 
                                  maxBadges={2}
                                  showSkeletonOnLoad={false}
                                />
                              </div>
                            )}

                            {/* Quote - Most Prominent Display */}
                            {renderQuoteDisplay(proposal)}

                            {/* Business Response - Generous Space */}
                            {proposal.response_message && (
                              <div className="mb-6 p-4 md:p-6 bg-white/80 rounded-lg border border-gray-100">
                                <div className="flex items-center gap-2 mb-2">
                                  <MessageCircle className="w-4 h-4 text-gray-600" />
                                  <h4 className="text-base font-semibold text-gray-800 mb-0">Business Response</h4>
                                </div>
                                <p className="text-sm md:text-base text-gray-700 leading-relaxed">{proposal.response_message}</p>
                              </div>
                            )}

                            {/* Response Date */}
                            {proposal.responded_at && (
                              <div className="text-center text-xs text-gray-500 mb-4">
                                <span>Proposed {formatDate(proposal.responded_at)}</span>
                              </div>
                            )}

                            {/* User Feedback Display */}
                            {proposal.user_feedback && (
                              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <h5 className="text-sm font-medium text-blue-900 mb-1">Your Feedback:</h5>
                                <p className="text-sm text-blue-800">{proposal.user_feedback}</p>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="space-y-2">
                              {statusDisplay.showFeedbackButton && (
                                <Button
                                  onClick={() => handleProposalFeedback(proposal)}
                                  className="w-full bg-primary hover:bg-primary/90 text-white py-2.5 md:py-3 text-sm md:text-base"
                                >
                                  <MessageCircle className="w-3.5 h-3.5 md:w-4 md:h-4 mr-2" />
                                  Respond to Proposal
                                </Button>
                              )}
                              
                              {proposal.status === 'USER_INTERESTED' && proposal.conversation_id && (
                                <Button
                                  onClick={() => {
                                    // Navigate to chat/conversation
                                    navigate(PATH.CONVERSATIONS_ID.replace(':conversationId', proposal.conversation_id));
                                  }}
                                  className="w-full bg-green-600 hover:bg-green-700 text-white py-2.5 md:py-3 text-sm md:text-base"
                                >
                                  <MessageCircle className="w-3.5 h-3.5 md:w-4 md:h-4 mr-2" />
                                  Continue Conversation
                                </Button>
                              )}
                              
                              <Button
                                onClick={() => {
                                  // Navigate to business profile
                                  navigate(PATH.SERVICE_VIEW.replace(':slug', proposal.business?.slug || ''));
                                }}
                                variant="outline"
                                className="w-full py-2.5 md:py-3 text-sm md:text-base"
                                disabled={!proposal.business?.slug}
                              >
                                View Business Profile
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </FadeIn>
                    );
                  })}
                </div>
              ) : (
                <Card className="text-center py-6 md:py-8 border-0 shadow-lg">
                  <CardContent>
                    <Users className="w-10 h-10 md:w-12 md:h-12 text-gray-400 mx-auto mb-3 md:mb-4" />
                    <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">No responses yet</h3>
                    <p className="text-gray-600 text-sm md:text-base">
                      Businesses will respond to your service request soon.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </FadeIn>
        </div>

        {/* Proposal Feedback Modal */}
        {selectedProposal && (
          <ProposalFeedbackModal
            isOpen={isFeedbackModalOpen}
            onClose={() => {
              setIsFeedbackModalOpen(false);
              setSelectedProposal(null);
            }}
            proposal={selectedProposal}
            onSuccess={handleFeedbackSuccess}
          />
        )}
      </motion.div>
    </Layout>
  );
};

export default ServiceRequestDetail;